import React, { useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Disc3, ShoppingBag } from "lucide-react";
import DraggableImage from "../DraggableImage/DraggableImage";
import { applyGradingDefaults } from "../../utils/ebayUtils";
import { generateDescription } from "../../utils/generateDescription";
import { parseDiscogsData } from "../../utils/parseDiscogsData";
import { validateUPC, getUPCFormat, formatUPC, checkUPCProblems, cleanUPC } from "../../utils/upcValidation";

// Helper to check if a URL is from our Supabase storage
const isSupabaseUrl = (url) => url && url.includes("supabase.co");

// Condition options for dropdowns - matching eBay format
const CONDITION_OPTIONS = [
  "Mint (M)",
  "Near Mint (NM or M-)",
  "Excellent (EX)",
  "Very Good Plus (VG+)",
  "Very Good (VG)",
  "Good Plus (G+)",
  "Good (G)",
  "Fair (F)",
  "Worn (W)"
];

const ListingCard = ({ id, formData, setFormData, images, onRemoveImage, onUpdatePricing, originalReleaseData, showValidationErrors }) => {
  console.log(`🔍 ListingCard ${id} - images:`, images);
  const { isOver, setNodeRef } = useDroppable({ id });

  // Pricing state
  const [prices, setPrices] = useState({
    low: '',
    med: '19.99',
    high: ''
  });
  const [selectedPriceType, setSelectedPriceType] = useState('med');
  const [showPricingReasoning, setShowPricingReasoning] = useState(false);
  const [description, setDescription] = useState(formData.description || '');
  const [title, setTitle] = useState(formData.title || '');
  const [upcOverride, setUpcOverride] = useState(formData.upc || '');
  const [showUpcSection, setShowUpcSection] = useState(false);
  const [showUpcInput, setShowUpcInput] = useState(false);

  // Initialize price in formData on component mount
  React.useEffect(() => {
    if (!formData.price) {
      setFormData({
        ...formData,
        price: prices[selectedPriceType]
      });
    }
  }, []); // Empty dependency array to run only on mount

  // Update prices when parent provides pricing data
  React.useEffect(() => {
    if (formData.pricingData) {
      setPrices({
        low: formData.pricingData.price_range?.conservative || '',
        med: formData.pricingData.suggested_price || '',
        high: formData.pricingData.price_range?.aggressive || ''
      });
      setPricingReasoning(formData.pricingData.rationale || '');
      // Set the med price as selected and update formData
      setSelectedPriceType('med');
      setFormData({
        ...formData,
        price: formData.pricingData.suggested_price || formData.price
      });
    }
  }, [formData.pricingData]);

  // Keep local title state in sync with formData.title
  React.useEffect(() => {
    if (formData.title && formData.title !== title) {
      setTitle(formData.title);
    }
  }, [formData.title]);

  // Keep local description state in sync with formData.description
  React.useEffect(() => {
    if (formData.description && formData.description !== description) {
      setDescription(formData.description);
    }
  }, [formData.description]);

  // Pricing reasoning from AI engine
  const [pricingReasoning, setPricingReasoning] = useState("");

  // UPC validation
  const upcProblems = checkUPCProblems(formData.upc);
  const isUpcValid = validateUPC(formData.upc);
  const upcFormat = getUPCFormat(formData.upc);

  // Auto-show UPC section if UPC is problematic
  React.useEffect(() => {
    if (upcProblems.isProblematic) {
      setShowUpcSection(true);
      setShowUpcInput(true);
    }
  }, [upcProblems.isProblematic]);

  const handleConditionToggle = () => {
    const newCondition = formData.condition === "USED" ? "NEW" : "USED";
    const updated = applyGradingDefaults(formData, newCondition);
    let updatedFormData = {
      ...formData,
      ...updated,
      condition: newCondition, // For UI
      itemCondition: newCondition, // For backend logic
    };
    
    
    updatedFormData.description = generateDescription(updatedFormData);
    setFormData(updatedFormData);
  };

  const handleConditionChange = (conditionType, value) => {
    const updatedFormData = {
      ...formData,
      [conditionType]: value,
    };

    // For USED records, remove "No issues!" if any condition is below NM
    if (formData.condition === "USED") {
      const conditionsToCheck = {
        conditionMedia: conditionType === 'conditionMedia' ? value : formData.conditionMedia,
        conditionSleeve: conditionType === 'conditionSleeve' ? value : formData.conditionSleeve,
        conditionInlay: conditionType === 'conditionInlay' ? value : formData.conditionInlay
      };

      // Check if any condition is below NM
      const belowNMConditions = ['Excellent (EX)', 'Very Good Plus (VG+)', 'Very Good (VG)', 'Good Plus (G+)', 'Good (G)', 'Fair (F)', 'Worn (W)'];
      const hasConditionBelowNM = Object.values(conditionsToCheck).some(condition => 
        condition && belowNMConditions.includes(condition)
      );

      if (formData.description && formData.description.includes("No issues!")) {
        if (hasConditionBelowNM) {
          // Replace "No issues!" with "See pics!!" when conditions are below NM
          updatedFormData.description = formData.description.replace("No issues!", "See pics!!");
          setDescription(updatedFormData.description);
        }
      } else if (formData.description && formData.description.includes("See pics!!")) {
        // If conditions improve back to NM/M, replace "See pics!!" with "No issues!"
        if (!hasConditionBelowNM) {
          updatedFormData.description = formData.description.replace("See pics!!", "No issues!");
          setDescription(updatedFormData.description);
        }
      }
    }

    setFormData(updatedFormData);
  };

  const handlePriceChange = (priceType, value) => {
    setPrices(prev => ({
      ...prev,
      [priceType]: value
    }));
    
    // If this is the currently selected price type, also update formData
    if (priceType === selectedPriceType) {
      setFormData({
        ...formData,
        price: value
      });
    }
  };

  const handlePriceSelection = (priceType) => {
    setSelectedPriceType(priceType);
    // Update formData with selected price for eBay submission
    setFormData({
      ...formData,
      price: prices[priceType]
    });
  };

  const handleDescriptionChange = (e) => {
    const newDescription = e.target.value;
    setDescription(newDescription);
    setFormData({
      ...formData,
      description: newDescription
    });
  };

  const handleTitleChange = (e) => {
    const newTitle = e.target.value.slice(0, 80); // Limit to 80 characters for eBay
    setTitle(newTitle);
    setFormData({
      ...formData,
      title: newTitle
    });
  };

  const handleUpcChange = (e) => {
    const rawValue = e.target.value;
    setUpcOverride(rawValue);
    
    // Clean UPC for formData unless it's "n/a"
    const newUpc = rawValue.toLowerCase() === 'n/a' ? 'n/a' : cleanUPC(rawValue);
    setFormData({
      ...formData,
      upc: newUpc
    });
  };

  const setUpcToNA = () => {
    setUpcOverride('n/a');
    setFormData({
      ...formData,
      upc: 'n/a'
    });
  };

  const toggleUpcSection = () => {
    setShowUpcSection(!showUpcSection);
    if (!showUpcSection) {
      // When showing section, initialize with current UPC
      setUpcOverride(formData.upc || '');
    }
  };

  const toggleUpcInput = () => {
    setShowUpcInput(!showUpcInput);
    if (!showUpcInput) {
      // When showing input, initialize with current UPC
      setUpcOverride(formData.upc || '');
    }
  };

  const handleRemove = (photoId) => {
    if (onRemoveImage) {
      onRemoveImage(photoId);
    }
  };

  // Validation helper functions
  const isValidForSubmission = () => {
    return title.trim().length > 0 && 
           formData.price && 
           parseFloat(formData.price) > 0 &&
           images.length > 0 &&
           description.trim().length > 0 &&
           (!formData.upc || validateUPC(formData.upc) || formData.upc.toLowerCase() === 'n/a');
  };

  const getValidationErrors = () => {
    const errors = [];
    if (!title.trim()) errors.push("Title required");
    if (!formData.price || parseFloat(formData.price) <= 0) errors.push("Valid price required");
    if (images.length === 0) errors.push("At least one photo required");
    if (!description.trim()) errors.push("Description required");
    if (formData.upc && !validateUPC(formData.upc) && formData.upc.toLowerCase() !== 'n/a') {
      errors.push("Invalid UPC format - edit to fix or set to 'n/a'");
    }
    return errors;
  };

  // Helper functions to generate URLs
  const getDiscogsUrl = () => {
    return `https://www.discogs.com/release/${formData.releaseId}`;
  };

  const getEbayUrl = () => {
    return formData.upc
      ? `https://www.ebay.com/sch/i.html?_nkw=${formData.upc}`
      : `https://www.ebay.com/sch/i.html?_nkw=${encodeURIComponent(
          `${(formData.artist || []).join(" ")} ${
            formData.fullTitle
          } ${formData.releaseYear} vinyl`
        )}`;
  };


  return (
    <div
      ref={setNodeRef}
      className={`border border-zinc-700 p-5 rounded-2xl shadow-lg mb-6 bg-zinc-900 text-white transition-shadow ${
        isOver ? "shadow-blue-500/50 outline outline-2 outline-blue-500" : ""
      }`}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 mr-4">
          <input
            type="text"
            value={title}
            onChange={handleTitleChange}
            maxLength="80"
            className={`w-full text-lg font-semibold bg-transparent border text-white focus:outline-none focus:ring-1 rounded px-2 py-1 ${
              title.trim().length === 0 
                ? "border-red-500 focus:ring-red-500 focus:border-red-500" 
                : "border-zinc-600 focus:ring-blue-500 focus:border-blue-500"
            }`}
            placeholder="Enter eBay listing title..."
          />
          <div className="text-xs mt-1 flex justify-between">
            <span className={title.trim().length === 0 ? "text-red-400" : "text-zinc-500"}>
              {title.trim().length === 0 ? "Title required" : `${title.length}/80 characters`}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {formData.releaseId && (
            <a
              href={getDiscogsUrl()}
              target="_blank"
              rel="noopener noreferrer"
              title="View on Discogs (Ctrl+Click or Middle-Click to stay on this tab)"
              className="text-zinc-400 hover:text-white transition-colors"
            >
              <Disc3 size={16} />
            </a>
          )}
          <a
            href={getEbayUrl()}
            target="_blank"
            rel="noopener noreferrer"
            title="Search on eBay (Ctrl+Click or Middle-Click to stay on this tab)"
            className="text-zinc-400 hover:text-white transition-colors"
            >
            <ShoppingBag size={16} />
          </a>
        </div>

        <button
          className={`px-4 py-1 rounded text-white text-sm font-semibold transition ml-4 ${
            formData.condition === "USED"
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-green-600 hover:bg-green-700"
          }`}
          onClick={handleConditionToggle}
        >
          {formData.condition}
        </button>
      </div>



      <div className={`flex flex-wrap items-center gap-2 mb-4 p-2 rounded ${
        images.length === 0 ? "border border-red-500" : ""
      }`}>
        {formData.imageUrl && (
          <img
            src={formData.imageUrl}
            alt="Discogs cover art"
            title="Discogs Image"
            className="w-24 h-24 object-contain rounded border border-zinc-700 flex-shrink-0"
            draggable={false}
          />
        )}

        <SortableContext
          items={images.map((img) => img.photoId)}
          strategy={horizontalListSortingStrategy}
        >
          {images.map((img) => {
            let thumbSrc = "";
            let fullSrc = img.baseUrl || "";
            const url = img.baseUrl || img.thumbSrc || "";

            if (isSupabaseUrl(url)) {
              thumbSrc = `${url}?width=256&height=256`;
              fullSrc = url;
            } else if (url) {
              thumbSrc = `/api/google/photos/proxy?url=${encodeURIComponent(
                url
              )}&size=w256-h256-c`;
            }

            return (
              <div key={img.photoId} className="w-24 h-24 flex-shrink-0">
                <DraggableImage
                  id={img.photoId}
                  image={{ ...img, thumbSrc: thumbSrc }}
                  containerId={id}
                  onRemove={() => handleRemove(img.photoId)}
                  onPreviewClick={() => window.open(fullSrc, "_blank")}
                />
              </div>
            );
          })}
        </SortableContext>
        
        {showValidationErrors && images.length === 0 && (
          <div className="text-xs text-red-400 mt-1">
            ⚠️ At least one photo required for listing
          </div>
        )}
      </div>

      {/* Pricing Section */}
      <div className="mt-4">
        <label className="block text-xs font-medium text-zinc-400 mb-2">
          Price
        </label>
        <div className={`bg-zinc-800 border p-3 rounded ${
          !formData.price || parseFloat(formData.price) <= 0 
            ? "border-red-500" 
            : "border-zinc-600"
        }`}>
          <div className="flex gap-2 mb-3 items-end">
            {/* Low Price */}
            <div className="w-24">
              <div className="flex items-center gap-1 mb-1">
                <input
                  type="radio"
                  id={`${id}-price-low`}
                  name={`${id}-price-selection`}
                  checked={selectedPriceType === 'low'}
                  onChange={() => handlePriceSelection('low')}
                  className="text-blue-500 focus:ring-blue-500"
                />
                <label htmlFor={`${id}-price-low`} className="text-xs font-medium text-zinc-400">
                  Low
                </label>
              </div>
              <input
                type="text"
                value={prices.low}
                onChange={(e) => handlePriceChange('low', e.target.value)}
                placeholder="0.00"
                className="w-full px-2 py-1 bg-zinc-700 border border-zinc-600 rounded text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Medium Price */}
            <div className="w-24">
              <div className="flex items-center gap-1 mb-1">
                <input
                  type="radio"
                  id={`${id}-price-med`}
                  name={`${id}-price-selection`}
                  checked={selectedPriceType === 'med'}
                  onChange={() => handlePriceSelection('med')}
                  className="text-blue-500 focus:ring-blue-500"
                />
                <label htmlFor={`${id}-price-med`} className="text-xs font-medium text-zinc-400">
                  Med
                </label>
              </div>
              <input
                type="text"
                value={prices.med}
                onChange={(e) => handlePriceChange('med', e.target.value)}
                placeholder="0.00"
                className="w-full px-2 py-1 bg-zinc-700 border border-zinc-600 rounded text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* High Price */}
            <div className="w-24">
              <div className="flex items-center gap-1 mb-1">
                <input
                  type="radio"
                  id={`${id}-price-high`}
                  name={`${id}-price-selection`}
                  checked={selectedPriceType === 'high'}
                  onChange={() => handlePriceSelection('high')}
                  className="text-blue-500 focus:ring-blue-500"
                />
                <label htmlFor={`${id}-price-high`} className="text-xs font-medium text-zinc-400">
                  High
                </label>
              </div>
              <input
                type="text"
                value={prices.high}
                onChange={(e) => handlePriceChange('high', e.target.value)}
                placeholder="0.00"
                className="w-full px-2 py-1 bg-zinc-700 border border-zinc-600 rounded text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Show Pricing Reasoning Toggle */}
            <button
              type="button"
              onClick={() => setShowPricingReasoning(!showPricingReasoning)}
              className="text-xs text-blue-400 hover:text-blue-300 underline ml-4 mb-1"
            >
              {showPricingReasoning ? 'Hide' : 'Show'} Pricing Reasoning
            </button>
          </div>

          {/* Pricing Reasoning Content */}
          {showPricingReasoning && (
            <div className="mt-2 p-2 bg-zinc-900 border border-zinc-600 rounded">
              <p className="text-xs text-zinc-300">
                {pricingReasoning}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* UPC Section - Compact Toggle */}
      <div className="mt-4">
        <div className="flex items-center justify-between">
          <span className="text-xs text-zinc-400">
            UPC: <span className={`font-mono ${isUpcValid ? 'text-green-400' : upcProblems.isProblematic ? 'text-red-400' : 'text-zinc-300'}`}>
              {formData.upc || 'None'}
            </span>
            {upcProblems.isProblematic && <span className="text-red-400 ml-1">⚠️</span>}
          </span>
          <button
            type="button"
            onClick={toggleUpcSection}
            className="text-xs text-blue-400 hover:text-blue-300 underline"
          >
            {showUpcSection ? 'Hide UPC' : 'Edit UPC'}
          </button>
        </div>

        {showUpcSection && (
          <div className={`mt-2 bg-zinc-800 border p-2 rounded ${
            upcProblems.isProblematic ? "border-red-500" : "border-zinc-600"
          }`}>
            {!showUpcInput ? (
              // Display/Info mode
              <div>
                <div className="flex items-center justify-between text-xs mb-2">
                  <span className="text-zinc-400">Current UPC:</span>
                  <button
                    type="button"
                    onClick={toggleUpcInput}
                    className="text-blue-400 hover:text-blue-300 underline"
                  >
                    Edit
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white font-mono">
                    {formData.upc || 'No UPC'}
                  </span>
                  <span className={`text-xs ${isUpcValid ? 'text-green-400' : 'text-yellow-400'}`}>
                    {upcFormat}
                  </span>
                </div>
                {upcProblems.isProblematic && (
                  <div className="text-red-400 text-xs mt-1">
                    ⚠️ {upcProblems.reason}
                  </div>
                )}
              </div>
            ) : (
              // Edit mode - Single line
              <div>
                <div className="flex items-center gap-2 text-xs">
                  <label className="text-zinc-400 whitespace-nowrap">UPC:</label>
                  <input
                    type="text"
                    value={upcOverride}
                    onChange={handleUpcChange}
                    placeholder="Enter UPC or 'n/a'"
                    className="flex-1 bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-white font-mono focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={setUpcToNA}
                    className="px-2 py-1 bg-orange-600 hover:bg-orange-700 text-white rounded transition-colors whitespace-nowrap"
                  >
                    n/a
                  </button>
                  <button
                    type="button"
                    onClick={toggleUpcInput}
                    className="px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors"
                  >
                    ✓
                  </button>
                </div>
                <div className="mt-1 text-xs">
                  <span className={`${
                    !formData.upc ? 'text-zinc-500' :
                    formData.upc.toLowerCase() === 'n/a' ? 'text-green-400' :
                    validateUPC(formData.upc) ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {!formData.upc ? 'No UPC' :
                     formData.upc.toLowerCase() === 'n/a' ? 'Disabled for eBay' :
                     validateUPC(formData.upc) ? `Valid ${getUPCFormat(formData.upc)}` : 
                     `Invalid ${getUPCFormat(formData.upc)}`}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-4">
        <label className="block text-xs font-medium text-zinc-400 mb-1">
          Description
        </label>
        <textarea
          ref={(el) => {
            if (el) {
              el.style.height = 'auto';
              el.style.height = el.scrollHeight + 'px';
            }
          }}
          value={description}
          onChange={(e) => {
            handleDescriptionChange(e);
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
          }}
          placeholder="Enter description..."
          rows={2}
          className={`w-full text-sm bg-black border p-3 rounded text-zinc-200 focus:outline-none focus:ring-1 resize-none overflow-hidden ${
            !description.trim()
              ? "border-red-500 focus:ring-red-500 focus:border-red-500"
              : "border-zinc-700 focus:ring-blue-500 focus:border-transparent"
          }`}
        >
        </textarea>
        {!description.trim() && (
          <div className="text-xs text-red-400 mt-1">
            ⚠️ Description required
          </div>
        )}
      </div>

      {/* Condition Details - Compact single line */}
      {formData.condition && (
        <div className="mt-3 p-2 bg-zinc-800 rounded border border-zinc-600">
          <div className="flex items-center gap-3 text-xs">
            <span className="text-zinc-400 font-medium">Record:</span>
            <select
              value={formData.conditionMedia || (formData.condition === "NEW" ? "Mint (M)" : "Near Mint (NM or M-)")}
              onChange={(e) => handleConditionChange("conditionMedia", e.target.value)}
              className="px-2 py-1 bg-zinc-700 border border-zinc-600 rounded text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            >
              {CONDITION_OPTIONS.map((condition) => (
                <option key={condition} value={condition}>
                  {condition}
                </option>
              ))}
            </select>
            
            <span className="text-zinc-400 font-medium">Cover:</span>
            <select
              value={formData.conditionSleeve || (formData.condition === "NEW" ? "Mint (M)" : "Near Mint (NM or M-)")}
              onChange={(e) => handleConditionChange("conditionSleeve", e.target.value)}
              className="px-2 py-1 bg-zinc-700 border border-zinc-600 rounded text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            >
              {CONDITION_OPTIONS.map((condition) => (
                <option key={condition} value={condition}>
                  {condition}
                </option>
              ))}
            </select>
            
            <span className="text-zinc-400 font-medium">Inlay:</span>
            <select
              value={formData.conditionInlay || (formData.condition === "NEW" ? "Mint (M)" : "Near Mint (NM or M-)")}
              onChange={(e) => handleConditionChange("conditionInlay", e.target.value)}
              className="px-2 py-1 bg-zinc-700 border border-zinc-600 rounded text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            >
              {CONDITION_OPTIONS.map((condition) => (
                <option key={condition} value={condition}>
                  {condition}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Validation Summary - only show when submission attempted */}
      {showValidationErrors && !isValidForSubmission() && (
        <div className="mt-3 p-2 bg-red-900/20 border border-red-500/50 rounded">
          <div className="text-xs text-red-400 font-medium mb-1">
            ⚠️ Issues preventing eBay submission:
          </div>
          <ul className="text-xs text-red-300 list-disc list-inside">
            {getValidationErrors().map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ListingCard;
